import type { ContextSubject } from "./model";
import { findingRoute } from "../findings/findingRoute";

/** Page destinations and panel URLs share the same typed identity. */
export function contextRoute(subject: ContextSubject): { to: string; params?: Record<string, string>; search?: Record<string, string> } {
  if ("address" in subject) return { to: "/addresses/$id", params: { id: subject.address }, search: { chain: subject.chain, tab: "conduct" } };
  if (subject.kind === "situation") return findingRoute(subject.id, subject.scope);
  return subject.alarmKind === "watch"
    ? { to: "/watches/$id", params: { id: subject.id }, ...(subject.scope ? { search: { scope: subject.scope } } : {}) }
    : { to: "/alerts", search: { tab: "history", rule: subject.id } };
}

export function contextHref(subject: ContextSubject): string {
  const route = contextRoute(subject);
  const path = Object.entries(route.params ?? {}).reduce((path, [key, value]) => path.replace(`$${key}`, encodeURIComponent(value)), route.to);
  return `${path}${route.search ? `?${new URLSearchParams(route.search)}` : ""}`;
}

/** Only references, never API data or arbitrary hrefs, may be restored from a URL. */
export function parseContextSubject(value: unknown): ContextSubject | null {
  if (!value || typeof value !== "object") return null;
  const s = value as Record<string, unknown>;
  const text = (key: string) => typeof s[key] === "string" && s[key].trim() && s[key].length <= 512 ? s[key] as string : null;
  if (s.kind === "actor" || s.kind === "situations") {
    const address = text("address"), chain = text("chain");
    return address && chain ? { kind: s.kind, address, chain } : null;
  }
  const id = text("id"), scope = text("scope");
  if (!id) return null;
  if (s.kind === "situation") return { kind: s.kind, id, ...(scope ? { scope } : {}) };
  if (s.kind === "alarm" && (s.alarmKind === "watch" || s.alarmKind === "rule")) return { kind: s.kind, id, alarmKind: s.alarmKind, ...(scope ? { scope } : {}) };
  return null;
}
